/**
 * Messages — 1:1 direct message inbox + thread view.
 *
 * Layout:
 *   • Left column (always visible on desktop, drawer-style on mobile):
 *     conversation list with unread dot, last snippet, and peer name.
 *   • Right column (only when :conversationId is set in URL): the
 *     message thread with a compose bar at the bottom.
 *
 * Polls the thread every 8 s while it's open so replies feel live without
 * needing websockets.
 */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { AuthContext } from '../App';
import ToolSelector from '../components/ToolSelector';
import DmSearchBar from '../components/DmSearchBar';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { MessageCircle, Send, ArrowLeft, User as UserIcon, Inbox, LifeBuoy } from 'lucide-react';

const rel = (iso) => {
  if (!iso) return '';
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ''; }
};

const Avatar = ({ name, size = 36 }) => {
  const initials = (name || '?').trim().slice(0, 2).toUpperCase();
  return (
    <div
      className="rounded-full bg-primary/15 text-primary flex items-center justify-center font-bold flex-shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.35 }}
    >
      {initials}
    </div>
  );
};

// ---------- Conversation row (left rail) ----------------------------------
const ConversationRow = ({ conv, active, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(conv)}
    data-testid={`dm-conv-${conv.id}`}
    className={`w-full text-left rounded-xl px-3 py-2.5 flex items-center gap-3 transition-colors border ${
      active
        ? 'bg-primary/10 border-primary/40'
        : 'bg-card border-border hover:border-primary/40 hover:bg-muted/60'
    }`}
  >
    <Avatar name={conv.peer?.name} size={40} />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <div className="font-heading text-sm font-bold text-foreground truncate flex-1">
          {conv.peer?.name || 'Unknown'}
        </div>
        <span className="text-[10px] text-muted-foreground flex-shrink-0">{rel(conv.last_message_at)}</span>
      </div>
      <div className="mt-0.5 flex items-center gap-2">
        <div className="text-xs text-muted-foreground truncate flex-1">
          {conv.last_snippet || 'No messages yet — say hello.'}
        </div>
        {conv.unread && (
          <span
            className="w-2 h-2 rounded-full bg-primary flex-shrink-0"
            aria-label="Unread"
            data-testid={`dm-unread-${conv.id}`}
          />
        )}
      </div>
    </div>
  </button>
);

// ---------- Message thread (right pane) -----------------------------------
const MessageThread = ({ conversationId, currentUser }) => {
  const { API } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/dm/conversations/${conversationId}/messages`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load thread');
    }
  };

  useEffect(() => { setData(null); load(); }, [conversationId]);

  // Poll while the thread is open.
  useEffect(() => {
    if (!conversationId) return undefined;
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, [conversationId]);

  // Auto-scroll to newest message.
  useEffect(() => {
    if (data && bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [data?.messages?.length]);

  const send = async () => {
    if (!draft.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/dm/conversations/${conversationId}/messages`, { body: draft.trim() });
      setDraft('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Message failed');
    } finally { setSending(false); }
  };

  if (!data) {
    return <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  }

  const peer = data.conversation.peer;

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="dm-thread">
      {/* Peer header */}
      <div className="border-b border-border px-4 py-3 flex items-center gap-3 bg-card">
        <Avatar name={peer?.name} size={36} />
        <div className="min-w-0 flex-1">
          <div className="font-heading font-bold text-foreground truncate">{peer?.name || 'Unknown'}</div>
          <div className="text-[11px] text-muted-foreground">
            {peer?.role === 'admin' ? 'Admin' : 'Member'}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2" data-testid="dm-messages">
        {data.messages.length === 0 && (
          <div className="text-center text-sm text-muted-foreground py-10">
            No messages yet — send the first one below.
          </div>
        )}
        {data.messages.map((m) => {
          const mine = m.sender_id === currentUser.id;
          return (
            <div
              key={m.id}
              className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
              data-testid={`dm-msg-${m.id}`}
            >
              <div
                className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                  mine
                    ? 'bg-primary text-primary-foreground rounded-br-md'
                    : 'bg-muted text-foreground rounded-bl-md'
                }`}
              >
                {m.body}
                <div className={`mt-1 text-[10px] ${mine ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                  {rel(m.created_at)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Compose */}
      <div className="border-t border-border p-3 bg-card">
        <div className="flex items-end gap-2">
          <Textarea
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            placeholder={`Message ${peer?.name || 'this member'}…`}
            data-testid="dm-compose"
            className="text-sm resize-none"
          />
          <Button
            onClick={send}
            disabled={sending || !draft.trim()}
            className="rounded-full font-bold h-10 px-4 flex-shrink-0"
            data-testid="dm-send"
          >
            <Send className="w-3.5 h-3.5 mr-1" /> Send
          </Button>
        </div>
      </div>
    </div>
  );
};

// ---------- Page ----------------------------------------------------------
export default function Messages() {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dm/conversations`);
      setConversations(r.data.conversations || []);
    } catch { toast.error('Could not load inbox'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Refresh the conversation list every 15 s so unread + last snippet stay fresh.
  useEffect(() => {
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <ToolSelector />
      <div className="max-w-6xl mx-auto px-2 sm:px-4 lg:px-6 py-4">
        <div className="mb-3 flex items-center gap-2">
          <div className="h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center">
            <MessageCircle className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="eyebrow text-primary">Direct Messages</div>
            <h1 className="font-heading text-2xl font-extrabold text-foreground">Inbox</h1>
          </div>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('fda:open-report', { detail: { type: 'support' } }))}
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 hover:bg-muted transition-colors px-3 py-1.5 text-xs font-bold text-foreground"
            data-testid="messages-contact-support"
            title="Contact the Freeze Dry Academy support team"
          >
            <LifeBuoy className="w-3.5 h-3.5 text-primary" />
            Contact support
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 h-[calc(100vh-180px)] min-h-[520px]">
          {/* Left rail */}
          <aside
            className={`lg:col-span-4 pro-card p-2 overflow-y-auto ${
              conversationId ? 'hidden lg:block' : 'block'
            }`}
            data-testid="dm-conversation-list"
          >
            <div className="p-2 border-b border-border mb-2 sticky top-0 bg-card z-10">
              <DmSearchBar
                onExistingSelect={(id) => navigate(`/messages/${id}`)}
                onNewSelect={async (peerId) => {
                  try {
                    const r = await axios.post(`${API}/dm/conversations`, {
                      peer_id: peerId,
                      initial_message: 'Hi 👋',
                    });
                    const newId = r?.data?.conversation?.id;
                    if (newId) navigate(`/messages/${newId}`);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Could not start chat');
                  }
                }}
              />
            </div>
            {loading && <div className="p-4 text-sm text-muted-foreground">Loading…</div>}
            {!loading && conversations.length === 0 && (
              <div className="p-6 text-center text-sm text-muted-foreground">
                <Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" />
                No conversations yet. Open a member&apos;s profile in the forum to start one.
              </div>
            )}
            <div className="space-y-1.5">
              {conversations.map((c) => (
                <ConversationRow
                  key={c.id}
                  conv={c}
                  active={c.id === conversationId}
                  onOpen={(cc) => navigate(`/messages/${cc.id}`)}
                />
              ))}
            </div>
          </aside>

          {/* Right pane */}
          <section className={`lg:col-span-8 pro-card overflow-hidden flex flex-col min-h-0 ${
            conversationId ? 'flex' : 'hidden lg:flex'
          }`}>
            {conversationId ? (
              <>
                <button
                  type="button"
                  onClick={() => navigate('/messages')}
                  className="lg:hidden inline-flex items-center gap-1 text-xs font-bold text-muted-foreground px-4 py-2 border-b border-border"
                  data-testid="dm-back"
                >
                  <ArrowLeft className="w-3 h-3" /> Inbox
                </button>
                <MessageThread conversationId={conversationId} currentUser={user} />
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground p-6 text-center">
                <div>
                  <UserIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  Pick a conversation from the left, or message someone from their profile.
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
